import { useWeb3React } from '@web3-react/core'
import wld from 'assets/images/wld.png'
import accountImg from 'assets/svg/account.svg'
import power from 'assets/svg/power.svg'
import { useAccountDrawer } from 'components/AccountDrawer/MiniPortfolio/hooks'
import { AnimatePresence, motion } from 'framer-motion'
import useLast from 'hooks/useLast'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { setRecentConnectionDisconnected } from 'state/user/reducer'
import { shortenAddress } from 'utilities/src/addresses'

export function ModalDisconnect() {
  const activeWeb3 = useWeb3React()
  const switchingChain = useAppSelector((state) => state.wallets.switchingChain)
  const ignoreWhileSwitchingChain = useCallback(() => !switchingChain, [switchingChain])

  const [walletDrawerOpen, toggleWalletDrawer] = useAccountDrawer()

  const lastWeb3 = useLast(useWeb3React(), ignoreWhileSwitchingChain)
  const { account, connector } = useMemo(() => (activeWeb3.account ? activeWeb3 : lastWeb3), [activeWeb3, lastWeb3])

  const dispatch = useAppDispatch()

  const disconnect = useCallback(() => {
    connector.deactivate?.()
    connector.resetState()
    dispatch(setRecentConnectionDisconnected())

    toggleWalletDrawer()
  }, [connector, dispatch, toggleWalletDrawer])

  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleWalletDrawer()
      }
    }
    if (walletDrawerOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [walletDrawerOpen, toggleWalletDrawer])

  if (!account || !walletDrawerOpen) {
    return null
  }

  return (
    <AnimatePresence>
      {walletDrawerOpen && (
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="flex flex-col items-stretch min-w-[171px] gap-[2px] absolute top-[45px] right-0 p-[3px] bg-light2 rounded-xl shadow-lg"
        >
          <div className="flex flex-col gap-1 items-center rounded-t-[10px] rounded-b-[4px] bg-white pt-2 pb-[6px]">
            <div className="relative w-8 h-8 bg-red-100 rounded-full">
              <img src={accountImg} alt="account" width={33} height={32} loading="lazy" />

              <img src={wld} alt="wld" className="absolute w-[13px] h-[13px] right-0 bottom-0" />
            </div>

            <p className="text-sm text-[#008095]">{shortenAddress(account)}</p>
          </div>

          <div
            onClick={disconnect}
            className="cursor-pointer flex items-center gap-[6px] px-2 py-[6px] bg-white rounded-b-[10px] rounded-t-[4px]"
          >
            <img src={power} alt="power" />
            <p className="text-sm text-light8">Disconnect</p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
